import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Badge } from '@mui/material';
import Taskbar from '../os/Taskbar';
import DashboardWidget from '../os/DashboardWidget';
import DesktopGrid from '../os/DesktopGrid';
import OSWindow from '../os/OSWindow';
import TourRunner from '../common/TourRunner';
import { TourType } from '../../config/TourConfigurations';
import { useAuth } from '../../context/AuthContext';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../../services/api';
import { useImageBrightness } from '../../hooks/useImageBrightness';

/**
 * Props for the TrainerOSLayout component
 */
interface TrainerOSLayoutProps {
    /** The content to render within the OS window */
    children?: React.ReactNode;
}

/**
 * Layout component for the Trainer interface, similar to the Admin OS layout.
 * It provides a desktop-like environment for trainers.
 *
 * @param props - The props for the component
 * @returns The Trainer OS Layout UI
 */
const TrainerOSLayout: React.FC<TrainerOSLayoutProps> = ({ children }) => {
    const location = useLocation();
    const isDashboard = location.pathname === '/trainer/dashboard';
    const [currentTour, setCurrentTour] = useState<TourType | null>(null);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [orgLogo, setOrgLogo] = useState<string | null>(null);
    const [pendingQuestionsCount, setPendingQuestionsCount] = useState(0);

    useEffect(() => {
        if (user?.role !== 'trainer') {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Fetch Organization Logo
    useEffect(() => {
        const fetchOrgLogo = async () => {
            if (user?.organizationId) {
                try {
                    const orgId = typeof user.organizationId === 'object'
                        ? (user.organizationId as any)?._id
                        : user.organizationId;
                    const res = await api.get(`/api/organizations/${orgId}`);
                    if (res.data && res.data.logoUrl) {
                        setOrgLogo(res.data.logoUrl);
                    }
                } catch (err) {
                    console.error("Error fetching organization logo", err);
                }
            }
        };

        const fetchPendingQuestions = async () => {
            if (isAppVisible('questions')) {
                try {
                    const res = await api.get('/api/questions/count');
                    setPendingQuestionsCount(res.data.count);
                } catch (err) {
                    console.error("Error fetching pending questions count", err);
                }
            }
        };

        fetchOrgLogo();
        fetchPendingQuestions();
    }, [user?.organizationId, user?.authorizedApps, user?.preferences?.enabledApps]);

    const handleTourEnd = () => {
        setCurrentTour(null);
    };

    const startTour = (tourType: TourType) => {
        setCurrentTour(tourType);
    };

    const getTitle = () => {
        const path = location.pathname;
        if (path.includes('/questions')) return 'Questions';
        if (path.includes('/block-evaluation')) return 'Évaluation de Bloc';
        if (path.includes('/email')) return 'Email';
        if (path.includes('/apprenants')) return 'Mes Apprenants';
        if (path.includes('/settings')) return 'Paramètres';
        return 'Espace Formateur';
    };

    const background = user?.preferences?.osBackground || '/default-bg.jpg';
    const taskbarContrast = useImageBrightness(background);

    // Helper function to check if app should be visible
    const isAppVisible = (appId: string) => {
        // @ts-ignore - Dynamic access to authorizedApps
        const isAuthorized = user?.authorizedApps?.[appId] ?? false;
        // @ts-ignore - Dynamic access to enabledApps
        const isEnabled = user?.preferences?.enabledApps?.[appId] ?? true;
        return isAuthorized && isEnabled;
    };

    return (
        <Box
            id="trainer-os-layout"
            sx={{
                height: '100vh',
                width: '100vw',
                backgroundImage: `url(${background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <TourRunner tourType={currentTour} onTourEnd={handleTourEnd} />

            {/* Desktop Area */}
            <Box id="desktop-area" sx={{ p: 4, height: '100%', position: 'relative', zIndex: 1 }}>
                {/* Logo Area */}
                <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img src="/logo.png" alt="Edu-JALM Logo" style={{ height: 60, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }} />
                    {orgLogo && (
                        <>
                            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 'bold', mx: 1 }}>X</Typography>
                            <img src={orgLogo} alt="Organization Logo" style={{ height: 50, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))', maxWidth: 150, objectFit: 'contain' }} />
                        </>
                    )}
                </Box>

                {/* Widget Area */}
                <Box id="dashboard-widget" sx={{ position: 'absolute', top: 40, right: 40 }}>
                    <DashboardWidget />
                </Box>

                {/* Desktop Icons Grid */}
                <DesktopGrid
                    icons={[
                        {
                            id: 'questions',
                            label: 'Questions',
                            icon: (
                                <Badge badgeContent={pendingQuestionsCount} color="error" overlap="circular" sx={{ '& .MuiBadge-badge': { right: -5, top: -5 } }}>
                                    <QuestionAnswerIcon />
                                </Badge>
                            ),
                            path: '/questions'
                        },
                        { id: 'block-evaluation', label: 'Évaluations', icon: <AssignmentIcon />, path: '/block-evaluation' },
                        { id: 'email', label: 'Email', icon: <EmailIcon />, path: '/email' },
                        { id: 'apprenants', label: 'Mes Apprenants', icon: <PeopleIcon />, path: '/trainer/apprenants' },
                        { id: 'settings', label: 'Paramètres', icon: <SettingsIcon />, path: '/settings' },
                    ].filter(item => isAppVisible(item.id))}
                    userPreferences={user?.preferences}
                />
            </Box>

            {/* Window Overlay */}
            {!isDashboard && children && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
                    <OSWindow
                        title={getTitle()}
                        initialMaximized={true}
                    >
                        {children}
                    </OSWindow>
                </Box>
            )}

            {/* Taskbar */}
            <Box id="taskbar">
                <Taskbar onStartTour={startTour} contrastMode={taskbarContrast} />
            </Box>
        </Box>
    );
};

export default TrainerOSLayout;
